/**
 * NormalizationWorkerService
 *
 * Gestiona el Web Worker de normalización con API basada en promesas.
 * Soporta progreso, cancelación y fallback al hilo principal.
 *
 * @module services/NormalizationWorkerService
 * @version 1.0.0 — F6.02
 */

import type { ExtractedInfrastructure, ProcessedInfrastructure } from '../types/processing';
import type {
  WorkerRequest,
  WorkerResponse,
  WorkerResultMessage,
} from '../workers/normalization.worker';
import type { NormalizationStats } from '../hooks/useNormalization';

// ============================================================================
// TIPOS
// ============================================================================

export interface NormalizationWorkerOptions {
  /** Callback de progreso (processed, total) */
  onProgress?: (processed: number, total: number) => void;
  /** Tamaño de lote para reportar progreso */
  batchSize?: number;
}

export interface NormalizationWorkerResult {
  items: ProcessedInfrastructure[];
  stats: NormalizationStats;
}

// ============================================================================
// SERVICE
// ============================================================================

export class NormalizationWorkerService {
  private worker: Worker | null = null;
  private requestCounter = 0;

  /**
   * ¿Está disponible Web Worker en este entorno?
   */
  static isAvailable(): boolean {
    return typeof Worker !== 'undefined';
  }

  /**
   * Inicia el worker (lazy — se crea en la primera llamada).
   */
  private ensureWorker(): Worker {
    if (!this.worker) {
      this.worker = new Worker(
        new URL('../workers/normalization.worker.ts', import.meta.url),
        { type: 'module' }
      );
    }
    return this.worker;
  }

  /**
   * Procesa infraestructuras en el Web Worker.
   * Devuelve promesa con resultados + estadísticas.
   */
  async normalize(
    items: ExtractedInfrastructure[],
    ineCode?: string,
    options?: NormalizationWorkerOptions,
  ): Promise<NormalizationWorkerResult> {
    const worker = this.ensureWorker();
    const requestId = `req-${++this.requestCounter}-${Date.now()}`;

    return new Promise<NormalizationWorkerResult>((resolve, reject) => {
      const handler = (event: MessageEvent<WorkerResponse>) => {
        const msg = event.data;
        if (msg.requestId !== requestId) return;

        switch (msg.type) {
          case 'progress':
            options?.onProgress?.(msg.processed, msg.total);
            break;

          case 'result': {
            worker.removeEventListener('message', handler);
            worker.removeEventListener('error', errorHandler);
            const result = msg as WorkerResultMessage;
            resolve({
              items: result.items as ProcessedInfrastructure[],
              stats: result.stats,
            });
            break;
          }

          case 'error':
            worker.removeEventListener('message', handler);
            worker.removeEventListener('error', errorHandler);
            reject(new Error(msg.message));
            break;
        }
      };

      const errorHandler = (err: ErrorEvent) => {
        worker.removeEventListener('message', handler);
        worker.removeEventListener('error', errorHandler);
        reject(new Error(err.message || 'Worker error'));
      };

      worker.addEventListener('message', handler);
      worker.addEventListener('error', errorHandler);

      const request: WorkerRequest = {
        type: 'normalize',
        requestId,
        items,
        ineCode,
        batchSize: options?.batchSize,
      };
      worker.postMessage(request);
    });
  }

  /**
   * Termina el worker y libera recursos.
   */
  terminate(): void {
    this.worker?.terminate();
    this.worker = null;
  }
}
